import { ChangeDetectionStrategy, Component, effect, inject, viewChild, ViewEncapsulation } from "@angular/core";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../../services/state/state.map";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SeEditableLogo } from "../se.editable.logo.component";
import { Item } from "../../../pojo/Item";
import { AngBridgeUtil } from "../../../util/ang.bridge.util";
import { SeEventCheckinStore } from "./se.event.checkin.store";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { ManageAttendanceComponent } from "./se.manage.attendance.component";
import { FormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25QrCodeComponent } from "../../../standalone/s25.qr.code.component";
import { S25PrintableComponent } from "../../../standalone/s25.printable.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";

@TypeManagerDecorator("s25-ng-event-checkin-dash")
@Component({
    standalone: true,
    selector: "s25-ng-event-checkin-dash",
    template: `
        <a s25-ng-router-link [stateName]="StateNames.studentEngagementEventDetails" [inheritParams]="true">
            Back to event
        </a>

        <h1>Check In</h1>
        <div class="event-header">
            @if (store.isLoading()) {
                Loading event details...
            } @else if (!!store.hasError()) {
                Error loading event details
            } @else {
                <se-editable-logo
                    [itemId]="store.eventId()"
                    [itemTypeId]="Item.Ids.Event"
                    [imageAttr]="store.eventImageUrl()"
                ></se-editable-logo>
                <div class="event-info">
                    <h2 class="event-title">{{ store.eventName() }}</h2>
                    <div class="event-details-grid">
                        <div class="detail-item">
                            <span class="detail-label">Date & Time</span>
                            <div class="detail-content">
                                <s25-ng-icon
                                    [type]="'calendarDate'"
                                    [size]="20"
                                    class="detail-icon"
                                    [ariaLabel]="false"
                                />
                                <span class="detail-value">{{ store.date() }}</span>
                            </div>
                        </div>
                        @if (store.locations()?.length) {
                            <div class="detail-item">
                                <span class="detail-label">Locations</span>
                                <div class="detail-content">
                                    @for (location of store.locations(); track location) {
                                        <div>
                                            <s25-ng-icon
                                                [type]="'locationMarker'"
                                                [size]="20"
                                                class="detail-icon"
                                                [ariaLabel]="false"
                                            />
                                            <span class="detail-value">{{ location }}</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>

        @if (!!store.occurrence()) {
            <div class="event-section">
                <h2>Check In Attendees</h2>
                <s25-ng-manage-attendees [occurrences]="[store.occurrence()]" [mode]="'checkin'" />
            </div>
        }

        <div class="event-section">
            <h2>Self Check-In</h2>
            <div class="card">
                <h3>Link</h3>
                <p>Attendees can use this link to check in</p>
                <a #checkInLink s25-ng-router-link [stateName]="StateNames.checkIn" [inheritParams]="true">
                    {{ checkInRouterLink()?.href() ?? "Check-In Link" }}
                </a>
            </div>
            <div class="card">
                <h3>QR Code</h3>
                <p>Attendees can use this QR code to check in</p>
                <s25-ng-qr-code [text]="checkInRouterLink()?.href()" [scale]="2" />
                <s25-ng-printable #checkInQR [hidden]="true">
                    <s25-ng-qr-code [text]="checkInRouterLink()?.href()" [width]="1080" />
                </s25-ng-printable>
                <div>
                    <s25-ng-button [type]="'outline'" (clicked)="checkInQR.print()">Print</s25-ng-button>
                </div>
            </div>
        </div>
    `,
    styles: `
        h1 {
            text-align: center;
            margin-bottom: 2rem;
        }

        .event-header {
            display: flex;
            gap: 3rem;
            align-items: flex-start;
            margin-bottom: 3rem;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e8e8e8;
            padding: 1.5rem;

            .event-info {
                flex: 1;
                min-width: 0;
            }

            .event-title {
                font-size: 2.8rem;
                color: #1a1a1a;
                font-weight: 800;
                line-height: 1.2;
            }

            .event-details-grid {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .detail-icon {
                flex-shrink: 0;
                margin-top: 0.2rem;
                color: #2196f3;
            }

            .detail-content {
                display: flex;
                align-items: center;
                gap: 0.25rem;
            }

            .detail-label {
                font-size: 0.875rem;
                color: #666;
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .detail-value {
                font-size: 1.125rem;
                color: #333;
                position: relative;
                top: 2px;
            }
        }

        .event-section {
            margin-bottom: 2rem;
            display: grid;
            gap: 1rem;

            & > h2 {
                color: #333;
                font-size: 1.8rem;
                border-bottom: 2px solid #f0f0f0;
            }
        }

        .card {
            display: grid;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e8e8e8;
            padding: 1.5rem;
        }

        @media print {
            :host s25-ng-printable.printing s25-ng-qr-code ::ng-deep canvas {
                width: min(100vw, 100vh) !important;
                height: min(100vw, 100vh) !important;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        S25RouterLinkDirective,
        SeEditableLogo,
        S25IconComponent,
        ManageAttendanceComponent,
        FormsModule,
        S25DropdownPaginatedModule,
        S25ToggleButtonModule,
        S25QrCodeComponent,
        S25PrintableComponent,
        S25ButtonComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeEventCheckinDashComponent {
    readonly checkInRouterLink = viewChild("checkInLink", { read: S25RouterLinkDirective });

    readonly store = inject(SeEventCheckinStore);
    readonly orgSecStore = inject(SeOrgSecurityStore);

    constructor() {
        AngBridgeUtil.init(this);
        effect(() => this.orgSecStore.setOrgId(this.store.orgId()));
    }

    protected readonly StateNames = S25States.names;
    protected readonly Item = Item;
}
